import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiClock,
  FiRefreshCw,
  FiShoppingCart,
  FiTag,
  FiPackage,
  FiHeart,
  FiZap,
  FiGift,
} from "react-icons/fi";
import "../../Styles/Offers.css";
import "../../Styles/ProductCard.css";
import dhiramsymbol from "../../assests/dhirams-png.webp";
import OfferCountdown from "../Common/OfferCountdown";
import axiosInstance from "../../utils/AxiosConfig";
import { showError, showSuccess } from "../../utils/Toast";
import {
  DEFAULT_PRODUCT_IMAGE,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import { getCartRequest } from "../../Containers/Home/Home-slice";
import { getwishlistRequest } from "../../Containers/Accounts/Account-slice";
import {
  getOfferRemainingSeconds as getOfferRemainingSecondsValue,
} from "../../utils/offerTime";
import { isUserLoggedIn, openAuthModal } from "../../utils/authModal";
import { getJsonStorageItem, setStorageItem } from "../../utils/browser";
import { getBuyerToken } from "../../utils/authStorage";
import { addGuestCartItem } from "../../utils/guestCart";
import { useInitialData } from "../../SSR/InitialDataContext";

const formatPrice = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatCurrency = (value) => `AED ${formatPrice(value)}`;
const WISHLIST_STORAGE_KEY = "wishlist_product_ids";
const FAVORITES_STORAGE_KEY = "favorites";

const toPositiveNumber = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const normalizeWishlistIds = (ids = []) =>
  Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((value) => toPositiveNumber(value))
        .filter((value) => value > 0),
    ),
  );

const getWishlistIdsFromStorage = () => {
  const wishlistIds = getJsonStorageItem(WISHLIST_STORAGE_KEY, null);
  if (Array.isArray(wishlistIds)) {
    return normalizeWishlistIds(wishlistIds);
  }
  return normalizeWishlistIds(getJsonStorageItem(FAVORITES_STORAGE_KEY, []));
};

const syncWishlistStorage = (ids = []) => {
  const normalizedIds = normalizeWishlistIds(ids);
  const serializedIds = JSON.stringify(normalizedIds);
  setStorageItem(WISHLIST_STORAGE_KEY, serializedIds);
  setStorageItem(FAVORITES_STORAGE_KEY, serializedIds);
};

const resolveOfferProductId = (offer) => {
  const candidates = [
    offer?.product_id,
    offer?.productId,
    offer?.product?.id,
    offer?.product?.product_id,
    offer?.product?.productId,
  ];
  for (const candidate of candidates) {
    const productId = toPositiveNumber(candidate);
    if (productId > 0) return productId;
  }
  return 0;
};

const normalizeResponseArray = (payload) => {
  const raw =
    payload?.data?.data ||
    payload?.data?.offers ||
    payload?.data?.result ||
    payload?.data ||
    [];
  return Array.isArray(raw) ? raw : [];
};

const normalizeOffer = (offer) => {
  const productName = offer?.product_name || offer?.name || offer?.product?.name || "Product";
  const categoryName = offer?.category_name || offer?.category?.name || "Offer";
  const originalPrice = Number(offer?.original_price || offer?.selling_price || offer?.product?.selling_price || 0);
  const discountAmount = Number(offer?.discount_amount || 0);
  const offerPrice = Number(offer?.offer_price || Math.max(originalPrice - discountAmount, 0));

  return {
    offer_id: Number(offer?.offer_id || offer?.id || 0),
    product_id: resolveOfferProductId(offer),
    product_name: String(productName),
    product_image: offer?.product_image || offer?.image_url || offer?.image || offer?.product?.image_url || offer?.product?.image || null,
    category_id: offer?.category_id === null || offer?.category_id === undefined ? null : Number(offer.category_id),
    category_name: String(categoryName),
    original_price: Number.isFinite(originalPrice) ? originalPrice : 0,
    discount_amount: Number.isFinite(discountAmount) ? discountAmount : 0,
    offer_price: Number.isFinite(offerPrice) ? offerPrice : 0,
    from_date: offer?.from_date || null,
    to_date: offer?.to_date || null,
    remaining_seconds: Number(offer?.remaining_seconds || 0),
    offer_title: String(offer?.offer_title || offer?.title || ""),
    offer_message: String(offer?.offer_message || ""),
    available_quantity: Number(offer?.available_quantity ?? offer?.stock_quantity ?? offer?.Available_Qty ?? 0),
  };
};

const normalizeCategory = (category) => ({
  id: category?.id === null || category?.id === undefined ? "uncategorized" : String(category.id),
  name: String(category?.name || ""),
  productCount: Number(category?.productCount || category?.product_count || 0),
});

export default function Offers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialData = useInitialData();
  const initialOffers = initialData?.url?.split("?")[0] === "/offers"
    ? initialData.offersPage?.offers
    : null;
  const loadedAtRef = useRef(initialData?.offersPage?.loadedAt || Date.now());
  const wishlistItems = useSelector((state) => state.Account?.wishlistdata?.data);
  const [offers, setOffers] = useState(() =>
    Array.isArray(initialOffers) ? initialOffers.map(normalizeOffer) : [],
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [isLoading, setIsLoading] = useState(!Array.isArray(initialOffers));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeOfferId, setActiveOfferId] = useState(null);
  const [wishlistedProductIds, setWishlistedProductIds] = useState(() => getWishlistIdsFromStorage());
  const [wishlistLoadingProductId, setWishlistLoadingProductId] = useState(null);

  const getOfferRemainingSeconds = useCallback(
    (offer, now = Date.now()) =>
      getOfferRemainingSecondsValue({
        toDate: offer?.to_date,
        remainingSeconds: offer?.remaining_seconds,
        loadedAt: loadedAtRef.current,
        now,
      }),
    [],
  );

  const fetchOffers = useCallback(async () => {
    const response = await axiosInstance.get("offers");
    const nextOffers = normalizeResponseArray(response).map(normalizeOffer);
    loadedAtRef.current = Date.now();
    setOffers(nextOffers);
    return nextOffers;
  }, []);

  const loadOffers = useCallback(async ({ silent = false } = {}) => {
    try {
      setErrorMessage("");
      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      await fetchOffers();
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to load offers. Please try again.";
      setOffers([]);
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fetchOffers]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  useEffect(() => {
    if (!isUserLoggedIn()) {
      setWishlistedProductIds([]);
      syncWishlistStorage([]);
      return;
    }
    dispatch(getwishlistRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    if (!Array.isArray(wishlistItems)) return;
    const nextIds = normalizeWishlistIds(wishlistItems.map((item) => item?.product_id ?? item?.id));
    setWishlistedProductIds(nextIds);
    syncWishlistStorage(nextIds);
  }, [wishlistItems]);

  const availableOffers = useMemo(
    () => offers.filter((offer) => {
      const remainingSeconds = getOfferRemainingSeconds(offer);
      return remainingSeconds === null || remainingSeconds > 0;
    }),
    [getOfferRemainingSeconds, offers],
  );

  const categories = useMemo(() => {
    const grouped = new Map();
    availableOffers.forEach((offer) => {
      const categoryId = String(offer.category_id ?? "uncategorized");
      if (!grouped.has(categoryId)) {
        grouped.set(categoryId, {
          id: offer.category_id ?? "uncategorized",
          name: offer.category_name || "Offer",
          productCount: 0,
        });
      }
      grouped.get(categoryId).productCount += 1;
    });
    return Array.from(grouped.values()).map(normalizeCategory);
  }, [availableOffers]);

  const filteredOffers = useMemo(() => {
    if (selectedCategoryId === "all") return availableOffers;
    return availableOffers.filter((offer) => String(offer.category_id) === String(selectedCategoryId));
  }, [availableOffers, selectedCategoryId]);

  const heroStats = useMemo(() => {
    const totalOffers = availableOffers.length;
    const totalSaved = availableOffers.reduce((sum, offer) => sum + offer.discount_amount, 0);
    return { totalOffers, totalSaved };
  }, [availableOffers]);

  const handleRefresh = () => loadOffers({ silent: true });
  const handleOfferExpired = () => loadOffers({ silent: true });

  const wishlistedProductIdSet = useMemo(
    () => new Set(normalizeWishlistIds(wishlistedProductIds)),
    [wishlistedProductIds],
  );

  const handleWishlistToggle = useCallback(async (offer, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (!isUserLoggedIn()) {
      openAuthModal();
      return;
    }

    const productId = resolveOfferProductId(offer);
    if (!productId) {
      showError("Product details are unavailable for this offer.");
      return;
    }
    if (wishlistLoadingProductId === productId) return;

    const isWishlisted = wishlistedProductIdSet.has(productId);
    try {
      setWishlistLoadingProductId(productId);
      if (isWishlisted) {
        const response = await axiosInstance.delete("removeFromWishlist", { data: { productid: productId } });
        setWishlistedProductIds((currentIds) => {
          const nextIds = currentIds.filter((id) => id !== productId);
          syncWishlistStorage(nextIds);
          return nextIds;
        });
        showSuccess(response?.data?.message || "Removed from wishlist");
      } else {
        const response = await axiosInstance.post("addToWishlist", { productid: productId });
        setWishlistedProductIds((currentIds) => {
          const nextIds = normalizeWishlistIds([...currentIds, productId]);
          syncWishlistStorage(nextIds);
          return nextIds;
        });
        showSuccess(response?.data?.message || "Added to wishlist");
      }
      dispatch(getwishlistRequest());
    } catch (error) {
      if (error?.response?.status === 401) openAuthModal();
      const message = error?.response?.data?.message || "Unable to update wishlist. Please try again.";
      showError(message);
    } finally {
      setWishlistLoadingProductId(null);
    }
  }, [dispatch, wishlistLoadingProductId, wishlistedProductIdSet]);

  const handleAddToCart = async (offer, event) => {
    event?.stopPropagation?.();
    const remainingSeconds = getOfferRemainingSeconds(offer);
    if (remainingSeconds !== null && remainingSeconds <= 0) {
      showError("This offer has expired.");
      loadOffers({ silent: true });
      return;
    }
    try {
      if (!offer.product_id) {
        showError("Product details are unavailable for this offer.");
        return;
      }
      setActiveOfferId(offer.offer_id);

      if (!getBuyerToken()) {
        addGuestCartItem({
          product_id: offer.product_id,
          quantity: 1,
          offer_id: offer.offer_id,
        });
        showSuccess("Added to cart");
        dispatch(getCartRequest());
        return;
      }

      await axiosInstance.post("addToCart", { product_id: offer.product_id, quantity: 1, offer_id: offer.offer_id });
      showSuccess("Added to cart");
      dispatch(getCartRequest());
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to add this offer to cart.";
      showError(message);
      if (/offer has expired/i.test(message)) loadOffers({ silent: true });
    } finally {
      setActiveOfferId(null);
    }
  };

  const openOfferProduct = (offer) => {
    const productId = resolveOfferProductId(offer);
    if (!productId) {
      showError("Product details are unavailable for this offer.");
      return;
    }
    navigate(`/product/${productId}`, {
      state: {
        item: {
          ...offer,
          id: productId,
          product_id: productId,
          price: offer.original_price,
          selling_price: offer.original_price,
          active_offer: {
            offer_id: offer.offer_id,
            original_price: offer.original_price,
            offer_discount_amount: offer.discount_amount,
            offer_price: offer.offer_price,
            offer_title: offer.offer_title || "",
            offer_message: offer.offer_message || "",
            from_date: offer.from_date,
            to_date: offer.to_date,
            remaining_seconds: offer.remaining_seconds,
          },
          offer_id: offer.offer_id,
          original_price: offer.original_price,
          offer_price: offer.offer_price,
          offer_discount_amount: offer.discount_amount,
        },
      },
    });
  };


  return (
    <div className="offers-page-v2">
      {/* Simple Top Banner */}
      <div className="offers-top-banner">
        <div className="offers-top-banner__content">
          <FiZap className="offers-top-banner__icon" />
          <span>Flash Sale! on Healthcare Products</span>
          <FiGift className="offers-top-banner__icon" />
        </div>
      </div>

      <div className="offers-container">
        {/* Simple Header */}
        <div className="offers-header">
          <div className="offers-header__left">
            <h1>Special Offers</h1>
            <p>Grab the best deals on medical supplies</p>
          </div>
          <div className="offers-header__right">
            <div className="offers-stats-pills">
              <div className="offers-stat-pill">
                <FiPackage />
                <span>{heroStats.totalOffers} Active</span>
              </div>
              <div className="offers-stat-pill">
                <FiTag />
                <span>Save {formatCurrency(heroStats.totalSaved)}</span>
              </div>
            </div>
            <button className="offers-refresh-btn" onClick={handleRefresh} disabled={isRefreshing}>
              <FiRefreshCw className={isRefreshing ? "spinning" : ""} />
            </button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="offers-categories">
          <button
            className={`offers-category-pill ${selectedCategoryId === "all" ? "active" : ""}`}
            onClick={() => setSelectedCategoryId("all")}
          >
            All Deals
            <span className="count">{availableOffers.length}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`offers-category-pill ${String(selectedCategoryId) === String(cat.id) ? "active" : ""}`}
              onClick={() => setSelectedCategoryId(String(cat.id))}
            >
              {cat.name}
              <span className="count">{cat.productCount}</span>
            </button>
          ))}
        </div>

        {/* Offers Grid */}
        {isLoading ? (
          <div className="offers-grid-v2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="offer-card-skeleton">
                <div className="skeleton-image" />
                <div className="skeleton-text" />
                <div className="skeleton-text short" />
                <div className="skeleton-button" />
              </div>
            ))}
          </div>
        ) : errorMessage ? (
          <div className="offers-empty">
            <FiTag size={48} />
            <h3>Oops! Something went wrong</h3>
            <p>{errorMessage}</p>
            <button onClick={handleRefresh}>Try Again</button>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="offers-empty">
            <FiPackage size={48} />
            <h3>No offers found</h3>
            <p>Check back later for new deals!</p>
            <button onClick={() => setSelectedCategoryId("all")}>View All</button>
          </div>
        ) : (
          <div className="offers-grid-v2">
            {filteredOffers.map((offer) => {
              const remainingSeconds = getOfferRemainingSeconds(offer);
              const isExpired = remainingSeconds !== null && Number(remainingSeconds) <= 0;
              const productId = resolveOfferProductId(offer);
              const isWishlisted = wishlistedProductIdSet.has(productId);
              const isWishlistLoading = wishlistLoadingProductId === productId;
              const discountPercent = offer.original_price > 0
                ? Math.round(((offer.original_price - offer.offer_price) / offer.original_price) * 100)
                : 0;

              return (
                <div
                  key={offer.offer_id}
                  className={`offer-card-v2 ${isExpired ? "expired" : ""}`}
                  onClick={() => openOfferProduct(offer)}
                >
                  {/* Discount Badge */}
                  {/* <div className="offer-card__badge">
                    {discountPercent}% OFF
                  </div> */}

                  {/* Wishlist Button */}
                  <button
                    className={`offer-card__wishlist ${isWishlisted ? "active" : ""}`}
                    onClick={(e) => handleWishlistToggle(offer, e)}
                    disabled={isWishlistLoading}
                  >
                    <FiHeart />
                  </button>

                  {/* Image */}
                  <div className="offer-card__image">
                    <img
                      src={withDefaultProductImage(offer.product_image)}
                      alt={offer.product_name}
                      onError={(e) => { e.currentTarget.src = DEFAULT_PRODUCT_IMAGE; }}
                    />
                  </div>

                  {/* Content */}
                  <div className="offer-card__content">
                    <span className="offer-card__category">{offer.category_name}</span>
                    <h3 className="offer-card__title">{offer.product_name}</h3>

                    {/* Price */}
                    <div className="offer-card__price">
                      <div className="offer-card__price-current">
                        <img src={dhiramsymbol} alt="AED" width={16} height={16} />
                        <span>{formatPrice(offer.offer_price)}</span>
                      </div>
                      <div className="offer-card__price-old">
                        {formatCurrency(offer.original_price)}
                      </div>
                    </div>

                    {/* Timer */}
                    <div className={`offer-card__timer ${isExpired ? "expired" : ""}`}>
                      <FiClock size={14} />
                      <OfferCountdown
                        toDate={offer.to_date}
                        remainingSeconds={offer.remaining_seconds}
                        loadedAt={loadedAtRef.current}
                        initialNow={initialOffers ? initialData.offersPage.loadedAt : null}
                        expiredText="Expired"
                        onExpire={handleOfferExpired}
                      />
                    </div>

                    {/* Actions */}
                    <div className="offer-card__actions">
                      <button
                        className="offer-card__btn offer-card__btn--outline"
                        onClick={(e) => { e.stopPropagation(); openOfferProduct(offer); }}
                      >
                        View
                      </button>
                      <button
                        className="offer-card__btn offer-card__btn--primary"
                        disabled={isExpired || activeOfferId === offer.offer_id}
                        onClick={(e) => handleAddToCart(offer, e)}
                      >
                        {activeOfferId === offer.offer_id ? (
                          "Adding..."
                        ) : (
                          <>
                            <FiShoppingCart size={16} />
                            {isExpired ? "Expired" : "Add"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
